import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Shield, Users, Edit, Save, X, Plus, Trash2, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UserRightsManagement() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  
  // Create/Edit Role Dialog
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [rolePermissions, setRolePermissions] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term) ||
      user.role?.toLowerCase().includes(term)
    );
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel using axios with credentials
      const [usersRes, rolesRes, permsRes] = await Promise.all([
        axios.get(`${API}/users/with-permissions`, { withCredentials: true }),
        axios.get(`${API}/roles/custom`, { withCredentials: true }),
        axios.get(`${API}/permissions/all`, { withCredentials: true })
      ]);

      setUsers(usersRes.data.users || []);
      setCustomRoles(rolesRes.data.roles || []);
      setAllPermissions(permsRes.data.permissions || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load user rights data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setSelectedPermissions(user.individual_permissions || []);
    setSelectedRoleId(user.custom_role_id || null);
  };

  const handleSaveUserPermissions = async () => {
    try {
      await axios.post(`${API}/permissions/update`, {
        user_id: editingUser.id,
        permissions: selectedPermissions,
        custom_role_id: selectedRoleId
      }, { withCredentials: true });

      toast.success('User permissions updated successfully');
      setEditingUser(null);
      fetchData();
    } catch (error) {
      console.error('Error updating permissions:', error);
      toast.error('Failed to update user permissions');
    }
  };

  const togglePermission = (permId) => {
    setSelectedPermissions(prev =>
      prev.includes(permId)
        ? prev.filter(p => p !== permId)
        : [...prev, permId]
    );
  };

  const toggleRolePermission = (permId) => {
    setRolePermissions(prev =>
      prev.includes(permId)
        ? prev.filter(p => p !== permId)
        : [...prev, permId]
    );
  };

  const handleCreateRole = () => {
    setEditingRole(null);
    setRoleName('');
    setRoleDescription('');
    setRolePermissions([]);
    setRoleDialogOpen(true);
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    setRoleName(role.name);
    setRoleDescription(role.description || '');
    setRolePermissions(role.permissions || []);
    setRoleDialogOpen(true);
  };

  const handleSaveRole = async () => {
    try {
      const url = editingRole
        ? `${API}/roles/custom/${editingRole.id}`
        : `${API}/roles/custom`;
      
      const method = editingRole ? 'put' : 'post';

      await axios[method](url, {
        name: roleName,
        description: roleDescription,
        permissions: rolePermissions
      }, { withCredentials: true });

      toast.success(`Role ${editingRole ? 'updated' : 'created'} successfully`);
      setRoleDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving role:', error);
      toast.error('Failed to save role');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role? Users with this role will lose these permissions.')) {
      return;
    }

    try {
      await axios.delete(`${API}/roles/custom/${roleId}`, { withCredentials: true });

      toast.success('Role deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting role:', error);
      toast.error('Failed to delete role');
    }
  };

  const toggleUserExpanded = (userId) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const getRoleColor = (role) => {
    const colors = {
      convenor: 'bg-purple-500/20 text-purple-400',
      captain: 'bg-blue-500/20 text-blue-400',
      merchant: 'bg-emerald-500/20 text-emerald-400',
      member: 'bg-slate-800/50 text-white'
    };
    return colors[role] || 'bg-slate-800/50 text-white';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-black/5 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading user rights...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-black/5 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/admin?tab=tools')} className="mb-4">
            ← Back to Admin Tools
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-2">
                <Shield className="w-8 h-8 text-yellow-500" />
                User Rights Management
              </h1>
              <p className="text-slate-400 mt-1">Manage user permissions and custom roles</p>
            </div>
          </div>
        </div>

        {/* Custom Roles Section */}
        <div className="bg-slate-900/50 rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">Custom Roles</h2>
            <Button onClick={handleCreateRole} className="bg-yellow-600 hover:bg-yellow-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Role
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {customRoles.map(role => (
              <div key={role.id} className="border border-white/5 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-white">{role.name}</h3>
                    {role.description && (
                      <p className="text-sm text-slate-400 mt-1">{role.description}</p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditRole(role)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteRole(role.id)}
                      className="text-red-400 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="mt-2">
                  <p className="text-xs text-slate-400 mb-1">Permissions ({role.permissions.length}):</p>
                  <div className="flex flex-wrap gap-1">
                    {role.permissions.slice(0, 3).map(perm => (
                      <span key={perm} className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded">
                        {perm.replace(/_/g, ' ')}
                      </span>
                    ))}
                    {role.permissions.length > 3 && (
                      <span className="text-xs text-slate-400">+{role.permissions.length - 3} more</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {customRoles.length === 0 && (
              <div className="col-span-full text-center py-8 text-slate-400">
                No custom roles created yet. Click &quot;Create Role&quot; to get started.
              </div>
            )}
          </div>
        </div>

        {/* Users Section */}
        <div className="bg-slate-900/50 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <Users className="w-5 h-5" />
              Users & Permissions ({filteredUsers.length} of {users.length})
            </h2>
          </div>
          
          {/* Search Input */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by name, email, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-800/50 border-slate-700"
              data-testid="user-search-input"
            />
          </div>

          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {filteredUsers.map(user => (
              <div key={user.id} className="border border-white/5 rounded-lg">
                {/* User Header */}
                <div
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-[#0a0f1a]"
                  onClick={() => toggleUserExpanded(user.id)}
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div>
                      {expandedUsers[user.id] ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-white">{user.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded ${getRoleColor(user.role)}`}>
                          {user.role}
                        </span>
                      </div>
                      <p className="text-sm text-slate-400">{user.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-400">
                        {user.permissions.length} permission{user.permissions.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditUser(user);
                    }}
                    className="ml-4"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                </div>

                {/* Expanded Details */}
                {expandedUsers[user.id] && (
                  <div className="px-4 pb-4 border-t border-white/5 bg-[#0a0f1a]">
                    <div className="mt-3 space-y-2">
                      <div>
                        <p className="text-xs font-semibold text-slate-300 mb-1">Current Permissions:</p>
                        <div className="flex flex-wrap gap-1">
                          {user.permissions.length > 0 ? (
                            user.permissions.map(perm => (
                              <span key={perm} className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">
                                {perm.replace(/_/g, ' ')}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-slate-400">No permissions</span>
                          )}
                        </div>
                      </div>
                      {user.custom_role_id && (
                        <div>
                          <p className="text-xs font-semibold text-slate-300 mb-1">Custom Role:</p>
                          <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
                            {customRoles.find(r => r.id === user.custom_role_id)?.name || 'Unknown Role'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
            {filteredUsers.length === 0 && searchTerm && (
              <div className="text-center py-8 text-slate-400">
                No users found matching "{searchTerm}"
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit User Permissions Dialog */}
      {editingUser && (
        <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Permissions: {editingUser.name}</DialogTitle>
              <DialogDescription>
                Manage individual permissions and assign custom roles
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Custom Role Assignment */}
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Assign Custom Role (Optional)
                </label>
                <select
                  value={selectedRoleId || ''}
                  onChange={(e) => setSelectedRoleId(e.target.value || null)}
                  className="w-full border border-slate-700 rounded-md px-3 py-2"
                >
                  <option value="">No custom role</option>
                  {customRoles.map(role => (
                    <option key={role.id} value={role.id}>
                      {role.name} ({role.permissions.length} permissions)
                    </option>
                  ))}
                </select>
              </div>

              {/* Individual Permissions */}
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Individual Permissions
                </label>
                <div className="space-y-2 max-h-60 overflow-y-auto border border-white/5 rounded-md p-3">
                  {allPermissions.map(perm => (
                    <label key={perm.id} className="flex items-start gap-2 cursor-pointer hover:bg-[#0a0f1a] p-2 rounded">
                      <input
                        type="checkbox"
                        checked={selectedPermissions.includes(perm.id)}
                        onChange={() => togglePermission(perm.id)}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium text-sm">{perm.id.replace(/_/g, ' ')}</div>
                        <div className="text-xs text-slate-400">{perm.name}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditingUser(null)}>
                  <X className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
                <Button onClick={handleSaveUserPermissions} className="bg-yellow-600 hover:bg-yellow-700">
                  <Save className="w-4 h-4 mr-1" />
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Create/Edit Role Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Edit' : 'Create'} Custom Role</DialogTitle>
            <DialogDescription>
              Define a role with specific permissions that can be assigned to users
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-1">
                Role Name
              </label>
              <input
                type="text"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                placeholder="e.g., Team Manager, Event Coordinator"
                className="w-full border border-slate-700 rounded-md px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-1">
                Description (Optional)
              </label>
              <textarea
                value={roleDescription}
                onChange={(e) => setRoleDescription(e.target.value)}
                placeholder="Describe the responsibilities of this role"
                rows={2}
                className="w-full border border-slate-700 rounded-md px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">
                Permissions
              </label>
              <div className="space-y-2 max-h-60 overflow-y-auto border border-white/5 rounded-md p-3">
                {allPermissions.map(perm => (
                  <label key={perm.id} className="flex items-start gap-2 cursor-pointer hover:bg-[#0a0f1a] p-2 rounded">
                    <input
                      type="checkbox"
                      checked={rolePermissions.includes(perm.id)}
                      onChange={() => toggleRolePermission(perm.id)}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-medium text-sm">{perm.id.replace(/_/g, ' ')}</div>
                      <div className="text-xs text-slate-400">{perm.name}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              <Button
                onClick={handleSaveRole}
                disabled={!roleName.trim()}
                className="bg-yellow-600 hover:bg-yellow-700"
              >
                <Save className="w-4 h-4 mr-1" />
                {editingRole ? 'Update' : 'Create'} Role
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
